import {
  ToggleLeft,
  SlidersHorizontal,
  Search,
  Beaker,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScoreBar, WeightedScoreBar } from './ScoreBar';
import { FeedbackButton } from './FeedbackButton';
import { useFeedback } from '@/features/review/hooks/useFeedback';
import type {
  EvaluationBreakdown,
  EvaluationCriterion,
  WeightedScore,
  BooleanResult,
  ScaleResult,
  TextMatchResult,
  PICOResult,
  BiasRiskResult,
} from '@/features/evaluation/types';
import type { CriterionType } from '@/features/criteria/types';

// =========================================
// CriteriaBreakdown — full score details
// =========================================

interface CriteriaBreakdownProps {
  evaluationId?: string;
  breakdown: EvaluationBreakdown;
  criteriaResults?: EvaluationCriterion[];
  className?: string;
}

const TYPE_ICONS: Record<CriterionType, typeof ToggleLeft> = {
  boolean: ToggleLeft,
  scale: SlidersHorizontal,
  text_match: Search,
  pico: Beaker,
  bias_risk: ShieldAlert,
};

const TYPE_LABELS: Record<CriterionType, string> = {
  boolean: 'Boolean',
  scale: 'Scale',
  text_match: 'Text Match',
  pico: 'PICO',
  bias_risk: 'Bias Risk',
};

export function CriteriaBreakdown({
  evaluationId,
  breakdown,
  criteriaResults,
  className,
}: CriteriaBreakdownProps) {
  const {
    getFeedbackForCriterion,
    submitFeedback,
    deleteFeedback,
  } = useFeedback(evaluationId ?? '');

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Criteria Breakdown</CardTitle>
          <Badge variant="outline" className="text-xs">
            {breakdown.criteria_count} criteria
          </Badge>
        </div>
        <ScoreBar
          score={breakdown.percentage}
          showPercentage
          showBucket
          size="lg"
        />
        <p className="text-xs text-slate-500 mt-1">
          Total: {breakdown.total_score.toFixed(1)} / {breakdown.max_possible.toFixed(1)} weighted points
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Separator />
        {breakdown.weighted_scores.map((ws) => {
          const criterionResult = criteriaResults?.find(
            (cr) => cr.criteria_id === ws.criteria_id
          );
          return (
            <CriterionRow
              key={ws.criteria_id}
              evaluationId={evaluationId}
              weightedScore={ws}
              criterionResult={criterionResult}
              existingFeedback={getFeedbackForCriterion(ws.criteria_id)}
              onSubmitFeedback={(input) => submitFeedback.mutate(input)}
              onDeleteFeedback={(id) => deleteFeedback.mutate(id)}
              isSubmitting={submitFeedback.isPending}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}

// =========================================
// CriterionRow — expandable row per criterion
// =========================================

interface CriterionRowProps {
  evaluationId?: string;
  weightedScore: WeightedScore;
  criterionResult?: EvaluationCriterion;
  existingFeedback?: import('@/features/review/hooks/useFeedback').LearningFeedback;
  onSubmitFeedback?: (input: import('@/features/review/hooks/useFeedback').SubmitFeedbackInput) => void;
  onDeleteFeedback?: (feedbackId: string) => void;
  isSubmitting?: boolean;
}

function CriterionRow({
  evaluationId,
  weightedScore,
  criterionResult,
  existingFeedback,
  onSubmitFeedback,
  onDeleteFeedback,
  isSubmitting,
}: CriterionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = TYPE_ICONS[weightedScore.criteria_type];

  const scoreAsPercentage = weightedScore.max_weighted > 0
    ? (weightedScore.raw_score / 100) * 100
    : 0;

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate">
            {weightedScore.criteria_name}
          </span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
            {TYPE_LABELS[weightedScore.criteria_type]}
          </Badge>
          {criterionResult?.result && 'confidence' in (criterionResult.result as unknown as Record<string, unknown>) && (
            <ConfidenceBadge confidence={(criterionResult.result as unknown as Record<string, unknown>).confidence as number} />
          )}
          {evaluationId && onSubmitFeedback && (
            <span className="shrink-0">
              <FeedbackButton
                evaluationId={evaluationId}
                criteriaId={weightedScore.criteria_id}
                originalScore={scoreAsPercentage}
                existingFeedback={existingFeedback}
                onSubmit={onSubmitFeedback}
                onDelete={onDeleteFeedback}
                isSubmitting={isSubmitting}
              />
            </span>
          )}
          <span className="ml-auto shrink-0">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )}
          </span>
        </div>
        <WeightedScoreBar
          rawScore={weightedScore.raw_score}
          weight={weightedScore.weight}
          maxWeighted={weightedScore.max_weighted}
          criteriaName=""
        />
      </button>

      {expanded && criterionResult && (
        <div className="ml-6 space-y-2 text-sm">
          {criterionResult.explanation && (
            <p className="text-slate-600 italic">
              {criterionResult.explanation}
            </p>
          )}
          <CriterionResultDetail
            type={weightedScore.criteria_type}
            result={criterionResult.result}
          />
          {criterionResult.passages.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-500">
                Cited passages ({criterionResult.passages.length})
              </p>
              {criterionResult.passages.map((passage, i) => (
                <blockquote
                  key={i}
                  className="border-l-2 border-indigo-300 pl-3 py-1 text-xs text-slate-600 bg-indigo-50/50 rounded-r"
                >
                  &ldquo;{passage.text}&rdquo;
                  {passage.page !== null && (
                    <span className="ml-1 text-slate-400">(p. {passage.page})</span>
                  )}
                </blockquote>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =========================================
// ConfidenceBadge — AI confidence indicator
// =========================================

function ConfidenceBadge({ confidence }: { confidence: number | undefined | null }) {
  if (confidence == null) return null;
  const pct = Math.round(confidence * 100);
  const color = confidence >= 0.8
    ? 'bg-emerald-100 text-emerald-700'
    : confidence >= 0.5
      ? 'bg-amber-100 text-amber-700'
      : 'bg-rose-100 text-rose-700';
  return (
    <span className={cn('px-1.5 py-0 text-[10px] font-medium rounded-full', color)}>
      {pct}%
    </span>
  );
}

// =========================================
// Result detail renderers per type
// =========================================

interface CriterionResultDetailProps {
  type: CriterionType;
  result: EvaluationCriterion['result'];
}

function CriterionResultDetail({ type, result }: CriterionResultDetailProps) {
  switch (type) {
    case 'boolean':
      return <BooleanDetail result={result as BooleanResult} />;
    case 'scale':
      return <ScaleDetail result={result as ScaleResult} />;
    case 'text_match':
      return <TextMatchDetail result={result as TextMatchResult} />;
    case 'pico':
      return <PICODetail result={result as PICOResult} />;
    case 'bias_risk':
      return <BiasRiskDetail result={result as BiasRiskResult} />;
    default:
      return null;
  }
}

function BooleanDetail({ result }: { result: BooleanResult }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'px-2 py-0.5 rounded text-xs font-medium',
          result.answer
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-rose-100 text-rose-700'
        )}
      >
        {result.answer ? 'Yes' : 'No'}
      </span>
      <span className="text-xs text-slate-500">
        Confidence: {Math.round(result.confidence * 100)}%
      </span>
    </div>
  );
}

function ScaleDetail({ result }: { result: ScaleResult }) {
  const pct = ((result.value - result.min) / (result.max - result.min)) * 100;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-500">
        <span>{result.min}</span>
        <span className="font-semibold text-slate-700">{result.value}</span>
        <span>{result.max}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}

function TextMatchDetail({ result }: { result: TextMatchResult }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-slate-500">
        Matched {result.matched_keywords.length}/{result.total_keywords} keywords (
        {Math.round(result.match_ratio * 100)}%)
      </p>
      <div className="flex flex-wrap gap-1">
        {result.matched_keywords.map((kw) => (
          <Badge key={kw} variant="secondary" className="text-[10px]">
            {kw}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function PICODetail({ result }: { result: PICOResult }) {
  const fields = ['population', 'intervention', 'comparator', 'outcome'] as const;
  return (
    <div className="grid grid-cols-2 gap-2">
      {fields.map((field) => {
        const item = result[field];
        return (
          <div
            key={field}
            className={cn(
              'p-2 rounded border text-xs',
              item.found ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'
            )}
          >
            <p className="font-medium capitalize text-slate-700">{field.charAt(0).toUpperCase()}</p>
            <p className="text-slate-600 mt-0.5">{item.detail || 'Not found'}</p>
          </div>
        );
      })}
    </div>
  );
}

function BiasRiskDetail({ result }: { result: BiasRiskResult }) {
  const riskColors: Record<string, string> = {
    low: 'bg-emerald-100 text-emerald-700',
    some_concerns: 'bg-amber-100 text-amber-700',
    high: 'bg-rose-100 text-rose-700',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Overall risk:</span>
        <span
          className={cn(
            'px-2 py-0.5 rounded text-xs font-medium',
            riskColors[result.overall_risk] ?? 'bg-slate-100 text-slate-700'
          )}
        >
          {result.overall_risk.replace(/_/g, ' ')}
        </span>
      </div>
      {result.items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span
            className={cn(
              'mt-0.5 w-2 h-2 rounded-full shrink-0',
              item.rating === 'low'
                ? 'bg-emerald-500'
                : item.rating === 'high'
                  ? 'bg-rose-500'
                  : 'bg-amber-500'
            )}
          />
          <div>
            <span className="font-medium text-slate-700">{item.label}</span>
            <p className="text-slate-500">{item.justification}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
